var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { ChildrenBlock, ChildrenBlockContext } from './ChildrenBlock.js';
import { BlockContext } from './BlockViewer.js';
import { shallowEqual } from './utils.js';
import { mergeRefs } from "react-merge-refs";
import ReactDOM from 'react-dom';
export const BlockWrapperPreRenderContext = createContext(false);
export function useBlockEffect(effect, deps) {
    const isPreRender = useContext(BlockWrapperPreRenderContext);
    useEffect(() => {
        if (!isPreRender) {
            return effect();
        }
    }, deps ? [...(deps !== null && deps !== void 0 ? deps : []), isPreRender] : undefined);
}
export const WrapperContext = createContext(undefined);
let BlockWarnedAboutMissuse = [];
export const BlockWrapperInstance = ({ children }) => {
    var _a, _b, _c, _d;
    //const [ready, setReady] = useState<boolean>(false);
    const ready = useRef(false);
    const prerender = useRef();
    if (!prerender.current) {
        prerender.current = document.createElement("div");
    }
    const isValidRef = useRef(false);
    const [state, dispatch] = useState(() => ({ component: undefined, props: {} }));
    const updateWrapper = useCallback((replacement) => {
        isValidRef.current = true;
        dispatch(replacement);
        ready.current = true;
    }, [dispatch]);
    const blockContext = useContext(BlockContext);
    const childrenContext = useContext(ChildrenBlockContext);
    const Component = state.component;
    const _e = (_a = state.props) !== null && _a !== void 0 ? _a : {}, { ref: stateRef } = _e, stateProps = __rest(_e, ["ref"]);
    const props = Object.assign({}, (stateProps !== null && stateProps !== void 0 ? stateProps : {}));
    const lastState = useRef({});
    let _f = (_b = blockContext === null || blockContext === void 0 ? void 0 : blockContext.gutenbergBlockProps) !== null && _b !== void 0 ? _b : {}, { ref: gutenbergRef } = _f, gutenbergBlockProps = __rest(_f, ["ref"]);
    const finalProps = (blockContext === null || blockContext === void 0 ? void 0 : blockContext.gutenbergBlockProps) ? Object.assign(Object.assign(Object.assign({}, gutenbergBlockProps), props), { className: [gutenbergBlockProps.className, props === null || props === void 0 ? void 0 : props.className].filter(e => e).join(' '), style: Object.assign(Object.assign({}, ((_c = gutenbergBlockProps.style) !== null && _c !== void 0 ? _c : {})), ((_d = props.style) !== null && _d !== void 0 ? _d : {})) }) : Object.assign({}, props);
    let refsList = [gutenbergRef, stateRef, childrenContext === null || childrenContext === void 0 ? void 0 : childrenContext.forwardedChildrenRef].filter(r => r);
    const ref = refsList.length > 0 ? mergeRefs(refsList) : undefined;
    if (!shallowEqual(lastState.current, finalProps)) {
        lastState.current = finalProps;
    }
    const contextValue = useMemo(() => {
        return ({ state: { component: Component, props: state.props }, dispatch: updateWrapper, hasBeenCalled: isValidRef });
    }, [Component, state.props, updateWrapper, gutenbergRef]);
    useLayoutEffect(() => {
        var _a, _b;
        if (!ready.current) {
            // @ts-ignore
            if (!isValidRef.current) {
                const blockName = (_a = blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block.name) !== null && _a !== void 0 ? _a : "UNKNOWN_BLOCK";
                if (!BlockWarnedAboutMissuse.includes(blockName)) {
                    console.error(`Block engine has applied default BlockWrapper to the block ${blockName}.
This is a fallback for when you don't provide a BlockWrapper to your block.
This is deprecated and will be removed in the future.
You must wrap your block inside a "<BlockWrapper.Div>" component at least.
Block source component: `, [(_b = blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block) === null || _b === void 0 ? void 0 : _b.Block]);
                    BlockWarnedAboutMissuse.push(blockName);
                }
            }
            // Note that in the future, we will throw an error here instead of applying a default wrapper
            //throw new Error("BlockWrapper is not ready yet");
            updateWrapper(Object.assign(Object.assign({}, state), { component: "div" }));
        }
    }, []);
    const child = useMemo(() => {
        var _a, _b;
        return (React.createElement(WrapperContext.Provider, { value: contextValue }, React.createElement(((_a = contextValue.state.component) !== null && _a !== void 0 ? _a : 'div'), Object.assign(Object.assign({}, ((_b = lastState.current) !== null && _b !== void 0 ? _b : {})), { ref }), React.createElement(BlockWrapperPreRenderContext.Provider, { value: !ready.current }, children))));
    }, [contextValue, children, lastState.current, ref, ready.current]);
    if (!ready.current) {
        return ReactDOM.createPortal(child, prerender.current);
    }
    return child;
};
Object.defineProperty(BlockWrapperInstance, 'name', {
    value: 'Wrapper',
});
const BlockWrapperBase = (_a, ref) => {
    var _b;
    var { blockWrapperComponent, children } = _a, props = __rest(_a, ["blockWrapperComponent", "children"]);
    const isPreRender = useContext(BlockWrapperPreRenderContext);
    const wrapperContext = useContext(WrapperContext);
    const dispatch = wrapperContext === null || wrapperContext === void 0 ? void 0 : wrapperContext.dispatch;
    const { component: currentComponent, props: currentProps } = (_b = wrapperContext === null || wrapperContext === void 0 ? void 0 : wrapperContext.state) !== null && _b !== void 0 ? _b : {};
    useLayoutEffect(() => {
        const _a = currentProps !== null && currentProps !== void 0 ? currentProps : {}, { ref: _ignored } = _a, previousProps = __rest(_a, ["ref"]);
        if (!wrapperContext || !dispatch) {
            return;
        }
        if (currentComponent !== blockWrapperComponent || !shallowEqual(previousProps, props)) {
            dispatch({ component: blockWrapperComponent, props: Object.assign(Object.assign({}, props), { ref }) });
        }
    }, [dispatch, blockWrapperComponent, props]);
    if (!wrapperContext) {
        // rtorrente 05/2022 - Disabling this throw to allow block with a BlockWrapper to be used in page with props and without gutenberg
        //throw new Error("Invalid state");
        // If we are not in wrapper context, block is called directly
        return React.createElement(blockWrapperComponent, props, children);
    }
    wrapperContext.hasBeenCalled.current = true;
    if (isPreRender) {
        return React.createElement(React.Fragment, null);
    }
    return React.createElement(Fragment, {}, children);
};
Object.defineProperty(BlockWrapperBase, 'name', {
    value: 'BlockWrapper',
});
let BlockWrapperComponent = React.forwardRef(BlockWrapperBase);
export const BlockWrapper = Object.assign(BlockWrapperComponent, {
    A: generateForTag('a'),
    Div: generateForTag('div'),
    P: generateForTag('p'),
    Button: generateForTag('button'),
    Form: generateForTag('form'),
    Span: generateForTag('span'),
    Ul: generateForTag('ul'),
    Li: generateForTag('li'),
    Input: generateForTag('input'),
    Select: generateForTag('select'),
    Option: generateForTag('option'),
    H1: generateForTag('h1'),
    H2: generateForTag('h2'),
    H3: generateForTag('h3'),
    H4: generateForTag('h4'),
    H5: generateForTag('h5'),
    H6: generateForTag('h6'),
    Img: generateForTag('img'),
    Label: generateForTag('label'),
    Textarea: generateForTag('textarea'),
    Table: generateForTag('table'),
    Tbody: generateForTag('tbody'),
    Td: generateForTag('td'),
    Tfoot: generateForTag('tfoot'),
    Th: generateForTag('th'),
    Thead: generateForTag('thead'),
    Tr: generateForTag('tr'),
    Video: generateForTag('video'),
    Audio: generateForTag('audio'),
    Canvas: generateForTag('canvas'),
    Summary: generateForTag('summary'),
    Area: generateForTag('area'),
    Base: generateForTag('base'),
    Main: generateForTag('main'),
    Nav: generateForTag('nav'),
    Header: generateForTag('header'),
    Footer: generateForTag('footer'),
    Section: generateForTag('section'),
    Article: generateForTag('article'),
    Without: generateForTag(React.Fragment, 'Fragment'),
    ChildrenBlocks: generateForTag(ChildrenBlock, 'ChildrenBlock'),
});
function generateForTag(tag, name = tag) {
    let type = BlockWrapperComponent;
    let component = (props, ref) => {
        const forwardedProps = useMemo(() => (Object.assign(Object.assign({}, props), { blockWrapperComponent: tag, ref })), [ref, props]);
        return React.createElement(type, forwardedProps);
    };
    Object.defineProperty(component, 'name', {
        value: 'BlockWrapper.' + name,
    });
    return React.forwardRef(component);
}

//# sourceMappingURL=BlockWrapper.js.map
